import React from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';

const psychedelicGlow = keyframes`
  0% { 
    transform: rotate(0deg) scale(1);
    text-shadow: 0 0 10px #ff00ff, 0 0 20px #ff00ff, 0 0 30px #ff00ff;
  }
  16.666% { 
    transform: rotate(60deg) scale(1.1);
    text-shadow: 0 0 10px #ff0080, 0 0 20px #ff0080, 0 0 30px #ff0080;
  }
  33.333% { 
    transform: rotate(120deg) scale(1);
    text-shadow: 0 0 10px #ff8000, 0 0 20px #ff8000, 0 0 30px #ff8000;
  }
  50% { 
    transform: rotate(180deg) scale(1.1);
    text-shadow: 0 0 10px #ffff00, 0 0 20px #ffff00, 0 0 30px #ffff00;
  }
  66.666% { 
    transform: rotate(240deg) scale(1);
    text-shadow: 0 0 10px #80ff00, 0 0 20px #80ff00, 0 0 30px #80ff00;
  }
  83.333% { 
    transform: rotate(300deg) scale(1.1);
    text-shadow: 0 0 10px #00ff80, 0 0 20px #00ff80, 0 0 30px #00ff80;
  }
  100% { 
    transform: rotate(360deg) scale(1);
    text-shadow: 0 0 10px #0080ff, 0 0 20px #0080ff, 0 0 30px #0080ff;
  }
`;

const pulse = keyframes`
  0%, 100% { 
    transform: scale(1);
  }
  50% { 
    transform: scale(1.2);
  }
`;

const AboutSection = styled.section`
  padding: ${theme.spacing[16]} ${theme.spacing[4]};
  background-color: ${theme.colors.gray[100]};
  overflow-x: hidden; /* Prevent horizontal overflow */
  width: 100%;
  scroll-margin-top: ${theme.navHeight.desktop};
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[10]} ${theme.spacing[4]};
    scroll-margin-top: ${theme.navHeight.mobile};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[12]};
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[8]};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    gap: ${theme.spacing[4]};
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing[6]};
  }
`;

const Title = styled.h2`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.gray[900]};
  padding-top: ${theme.spacing[10]};
  margin-bottom: ${theme.spacing[3]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['3xl']};
  }
`;

const Tagline = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.gray[600]};
  font-weight: ${theme.typography.fontWeight.medium};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.gray[700]};
  line-height: 1.6;
  margin-bottom: ${theme.spacing[6]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.base};
    margin-bottom: ${theme.spacing[4]};
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    gap: ${theme.spacing[3]};
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const FeatureIcon = styled.div`
  font-size: ${theme.typography.fontSize.xl};
  line-height: 1;
  margin-top: 2px;
  flex-shrink: 0;
  animation: ${psychedelicGlow} 3s ease-in-out infinite;
  transition: all 0.3s ease;
  
  &:hover {
    animation: ${pulse} 0.6s ease-in-out infinite;
    filter: brightness(1.2);
  }
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    
    &:hover {
      animation: none;
      transform: scale(1.1);
    }
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.gray[900]};
  margin-bottom: ${theme.spacing[1]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const FeatureDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.gray[600]};
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const CTAButton = styled.button`
  background-color: ${theme.colors.primary};
  color: white;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  padding: ${theme.spacing[4]} ${theme.spacing[8]};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${theme.spacing[2]};
  
  &:hover {
    background-color: #1e7354;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.base};
    padding: ${theme.spacing[3]} ${theme.spacing[6]};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: ${theme.borderRadius['2xl']};
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-height: 600px;
  
  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    max-height: 400px;
    order: -1;
  }
`;

const About: React.FC = () => {
  const features = [
    {
      icon: "🔐",
      title: "License Management",
      description: "Stay ahead of compliance with smart alerts for expiring licenses and jurisdiction-specific requirements."
    },
    {
      icon: "📊",
      title: "Sustainability Reporting",
      description: "Track and showcase water use, energy efficiency, and waste reduction. Earn badges for verified environmental impact."
    },
    {
      icon: "🧪",
      title: "Lab & Terpene Integration",
      description: "Upload lab results, visualize cannabinoid profiles, and make terpene data work for your brand."
    },
    {
      icon: "🏆",
      title: "Awards & Certifications",
      description: "Earn and display awards from trusted partners. Build credibility and stand out in the Grower Directory."
    },
    {
      icon: "📈",
      title: "Analytics Dashboard",
      description: "See how your profile performs. Get insights on visits, reviews, and strain engagement."
    },
    {
      icon: "📤",
      title: "OSHA & Compliance Tools",
      description: "Generate reports on the fly. Streamline inspections and meet state/federal requirements with less stress."
    },
    {
      icon: "🧬",
      title: "Strain Submission & Profiling",
      description: "Add your strains to our Cannabis Germplasm Repository. Connect your genetics with verified data and community reviews."
    },
    {
      icon: "🛠️",
      title: "Custom Grower Profile",
      description: "Tell your story. Highlight your growing practices, certifications, and sustainability ethos—all in a visual, mobile-friendly format."
    }
  ];

  return (
    <AboutSection id="about">
      <HeaderSection>
        <Title>Claim Your Grower Profile</Title>
        <Tagline>Your Grow, Verified. Your Story, Amplified.</Tagline>
      </HeaderSection>
      <Container>
        <Content>
          <Description>
            Take full control of your cannabis cultivation operation with a dashboard built for transparency, compliance, and credibility.
          </Description>
          <FeatureList>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureItem>
            ))}
          </FeatureList>
          <CTAButton onClick={() => window.open('https://dashboard.greengrowers.club', '_self')}>
            Start Your Dashboard
          </CTAButton>
        </Content>
        <ImageContainer>
          <img src="/images/grower-dashboard.png" alt="Cannabis grower using dashboard interface" />
        </ImageContainer>
      </Container>
    </AboutSection>
  );
};

export default About;
